import React from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { HttpService } from 'core/services/http';
import {
    Box, Container, Typography, Paper, CircularProgress,
    Alert, Chip, Stack, Divider, Button, Grid, Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const DECISION_CONFIG = {
    shortlisted: { label: 'Shortlisted', color: 'success' },
    rejected:    { label: 'Rejected',    color: 'error'   },
    hired:       { label: 'Hired',       color: 'primary' },
};
const decisionColor = (d) => DECISION_CONFIG[d]?.color ?? 'default';
const decisionLabel = (d) => DECISION_CONFIG[d]?.label ?? 'Pending';

const AVATAR_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f'];
const nameToColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const fmt = (dateStr, opts = { year: 'numeric', month: 'long', day: 'numeric' }) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', opts);
};

function TimelineStep({ done, label, date }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {done
                ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.15, flexShrink: 0 }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.15, flexShrink: 0 }} />
            }
            <Box>
                <Typography variant="body2" fontWeight={done ? 600 : 400} color={done ? 'text.primary' : 'text.disabled'}>
                    {label}
                </Typography>
                {done && date && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {fmt(date)}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

function JobseekerApplicationDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data: application, isLoading, isError, error } = useQuery(
        ['application', id],
        async () => {
            const service = new HttpService();
            return await service.get(`/job/apply/${id}`, null);
        }
    );

    const backButton = (
        <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/jobseeker/applications')}
            size="small"
            color="inherit"
            sx={{ color: 'text.secondary', textTransform: 'none', mb: 2 }}
        >
            Back to Applications
        </Button>
    );

    if (isLoading) return (
        <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} />
            </Box>
        </Container>
    );

    if (isError || !application) return (
        <Container maxWidth="lg">
            {backButton}
            <Alert severity="error">{error?.message || 'Application not found.'}</Alert>
        </Container>
    );

    const company = application.job?.company?.title || application.company_title || 'Unknown Company';
    const jobTitle = application.job?.title || 'Unknown Job';
    const salary = application.job?.pay_minimum && application.job?.pay_maximum
        ? `$${application.job.pay_minimum} – $${application.job.pay_maximum} / ${application.job.pay_rate}`
        : null;

    const timelineSteps = [
        { label: 'Application Submitted', done: true, date: application.created_at },
        { label: 'Viewed by Employer', done: !!application.viewed_by_employer_at, date: application.viewed_by_employer_at },
        { label: 'Decision Made', done: !!application.final_decision && application.final_decision !== 'pending', date: null },
    ];

    return (
        <Container maxWidth="lg">
            {backButton}

            <Grid container spacing={2.5}>
                {/* ── Main column ── */}
                <Grid item xs={12} md={8}>
                    {/* Job header */}
                    <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                variant="rounded"
                                sx={{
                                    width: 48, height: 48, fontSize: 18, fontWeight: 700,
                                    bgcolor: nameToColor(company), flexShrink: 0,
                                    border: '1px solid', borderColor: 'divider',
                                }}
                            >
                                {company.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                                        {jobTitle}
                                    </Typography>
                                    <Chip
                                        label={decisionLabel(application.final_decision)}
                                        color={decisionColor(application.final_decision)}
                                        size="small"
                                        sx={{ height: 20, fontSize: 11 }}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography variant="body2" color="text.secondary">{company}</Typography>
                                    {application.job?.address && (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <LocationOnOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                {application.job.address}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                                {salary && (
                                    <Typography variant="caption" color="text.secondary">{salary}</Typography>
                                )}
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Application body */}
                    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Stack spacing={2.5}>
                            {/* Applied date */}
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: 1, flexShrink: 0,
                                    bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <CalendarTodayIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">Applied</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {fmt(application.created_at, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Cover letter */}
                            {application.cover_letter && (
                                <>
                                    <Divider />
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                            <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="subtitle2" fontWeight={600}>Cover Letter</Typography>
                                        </Stack>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderRadius: 1.5 }}
                                        >
                                            <Typography variant="body2" lineHeight={1.8}>
                                                {application.cover_letter}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                </>
                            )}

                            {/* Employer notes */}
                            {application.decision_notes && (
                                <>
                                    <Divider />
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            Employer Notes
                                        </Typography>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, bgcolor: 'warning.50', whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderRadius: 1.5 }}
                                        >
                                            <Typography variant="body2" lineHeight={1.8}>
                                                {application.decision_notes}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Grid>

                {/* ── Sidebar ── */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={2}>
                        {/* Status timeline */}
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Application Progress
                            </Typography>
                            <Stack spacing={1.75} sx={{ mt: 1.5 }}>
                                {timelineSteps.map((step) => (
                                    <TimelineStep key={step.label} {...step} />
                                ))}
                            </Stack>
                            {application.final_decision && application.final_decision !== 'pending' && (
                                <>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">Decision</Typography>
                                        <Chip
                                            label={decisionLabel(application.final_decision)}
                                            color={decisionColor(application.final_decision)}
                                            size="small"
                                        />
                                    </Box>
                                </>
                            )}
                        </Paper>

                        {/* Job details */}
                        {application.job && (
                            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Job Details
                                </Typography>
                                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                                    {[
                                        { icon: <WorkOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />, value: application.job.jobtypes?.map(j => j.title).join(', ') || 'N/A' },
                                        { icon: <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />, value: application.job.company?.city || application.job.address || 'N/A' },
                                        ...(salary ? [{ icon: null, value: salary }] : []),
                                    ].map((item, i) => (
                                        <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                            {item.icon && <Box sx={{ mt: 0.2 }}>{item.icon}</Box>}
                                            <Typography variant="body2" color="text.secondary">{item.value}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    endIcon={<OpenInNewIcon fontSize="small" />}
                                    onClick={() => navigate(`/job/${application.job.id}`)}
                                    sx={{ mt: 2, textTransform: 'none' }}
                                >
                                    View Job Posting
                                </Button>
                            </Paper>
                        )}

                        {/* CV submitted */}
                        {(application.cv || application.cv_file) && (
                            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Submitted CV
                                </Typography>
                                <Stack spacing={1} sx={{ mt: 1 }}>
                                    {application.cv && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <DescriptionOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                            <Typography variant="body2">
                                                {application.cv.title || 'Untitled CV'}
                                            </Typography>
                                            <VisibilityIcon
                                                sx={{ fontSize: 14, color: 'primary.main', cursor: 'pointer', ml: 'auto' }}
                                                onClick={() => navigate(`/cv/${application.cv.id}`)}
                                            />
                                        </Stack>
                                    )}
                                    {application.cv_file && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <DescriptionOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                            <Typography variant="body2">
                                                {application.cv_file.title || 'Uploaded File'}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </Grid>
            </Grid>
        </Container>
    );
}

export default JobseekerApplicationDetailsPage;
